<script setup lang="ts">
import { useUserStore } from '@/store/userStore';

const props = defineProps<{
  schoolType: string | null | undefined
}>();

const emit = defineEmits<{
  'open-menu': []
}>();

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();

const isTertiary = computed(() => props.schoolType?.toLowerCase() === 'tertiary');

interface TabItem {
  label: string
  icon: string
  routeName?: string
  action?: () => void
}

const tabs = computed<TabItem[]>(() => {
  // CBT-only portals collapse the bottom nav to just the CBT surface (#326).
  if (userStore.isCbtOnlyStudentPortal) {
    return [
      {
        label: 'CBT',
        icon: 'pi-pencil',
        routeName: 'student-cbt',
      },
      {
        label: 'More',
        icon: 'pi-ellipsis-h',
        action: () => emit('open-menu'),
      },
    ];
  }

  return [
    {
      label: 'Home',
      icon: 'pi-home',
      routeName: 'student-home',
    },
    isTertiary.value
      ? {
        label: 'Courses',
        icon: 'pi-book',
        routeName: 'student-courses',
      }
      : {
        label: 'Results',
        icon: 'pi-chart-bar',
        routeName: 'student-result',
      },
    {
      label: 'Billing',
      icon: 'pi-wallet',
      routeName: 'student-billing',
    },
    {
      label: 'CBT',
      icon: 'pi-pencil',
      routeName: 'student-cbt',
    },
    {
      label: 'More',
      icon: 'pi-ellipsis-h',
      action: () => emit('open-menu'),
    },
  ];
});

const isActive = (tab: TabItem): boolean => {
  if (!tab.routeName) return false;
  return route.name === tab.routeName;
};

const handleTap = (tab: TabItem) => {
  if (tab.action) {
    tab.action();
    return;
  }
  if (tab.routeName) {
    router.push({ name: tab.routeName });
  }
};
</script>

<template>
  <nav
    class="fixed bottom-0 left-0 right-0 z-40 flex lg:hidden bg-white border-t border-gray-200 pb-[env(safe-area-inset-bottom)]"
  >
    <button
      v-for="tab in tabs"
      :key="tab.label"
      type="button"
      class="flex flex-1 flex-col items-center justify-center gap-1 py-2 min-h-[56px] transition-colors"
      :class="isActive(tab) ? 'text-primary' : 'text-gray-500'"
      @click="handleTap(tab)"
    >
      <span
        class="flex items-center justify-center w-11 h-11 rounded-full transition-colors"
        :class="isActive(tab) ? 'bg-primary text-white' : ''"
      >
        <i class="pi text-lg" :class="tab.icon"></i>
      </span>
      <span class="text-[10px] font-medium leading-tight">{{ tab.label }}</span>
    </button>
  </nav>
</template>
